/**
 * HTTP client — bearer-token auth + 401 invalidation. All API calls
 * (including binary downloads via getBlob and multipart via postFormData)
 * must go through this — never hand-roll fetch + localStorage token reads.
 */

// In dev: /api/* is proxied to localhost:8000 by Vite (stripping /api prefix)
// In prod: set VITE_API_URL to the full backend URL
const API_BASE = import.meta.env.VITE_API_URL || '/api';

export interface ApiResponse<T = any> {
  data: T;
  status: number;
}

/**
 * Error thrown for every non-OK response. `message` is the FastAPI `detail`
 * string (or the HTTP statusText when the body has none); `status` is the
 * HTTP status code. This is the ONLY error shape this client produces —
 * there is no axios here, so `e.response?.data?.detail` never exists.
 * Unwrap with `e.message` (and `e.status` for code-specific handling).
 */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export type ResponseType = 'json' | 'blob' | 'text';

export interface RequestOptions {
  /** Override response parsing. Default: 'json' if Content-Type is JSON, else 'text'. */
  responseType?: ResponseType;
  /** AbortSignal for request cancellation (e.g. on component unmount). */
  signal?: AbortSignal;
  /** Extra headers (merged with auth header). */
  headers?: Record<string, string>;
}

class HttpClient {
  private baseURL: string;

  constructor(baseURL: string = API_BASE) {
    this.baseURL = baseURL;
  }

  private getToken(): string | null {
    return localStorage.getItem('cm_token');
  }

  /**
   * Low-level fetch wrapper that applies the bearer-token + 401 invalidation
   * but does NOT parse the body or throw on non-OK responses. Callers are
   * responsible for inspecting `response.ok` and consuming the body. Use
   * this for streaming or unusual response shapes; otherwise use `getBlob`
   * (binary) or one of the typed methods (`get`, `post`, …).
   */
  async requestRaw(
    url: string,
    method = 'GET',
    body?: any,
    opts: RequestOptions = {},
  ): Promise<Response> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    const headers: Record<string, string> = { ...(opts.headers || {}) };
    const token = this.getToken();

    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (body && !(body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const init: RequestInit = { method, headers };
    if (body) {
      init.body = body instanceof FormData ? body : JSON.stringify(body);
    }
    if (opts.signal) {
      init.signal = opts.signal;
    }

    const response = await fetch(fullUrl, init);

    // Handle 401 — clear token (auth context picks this up on next render)
    if (response.status === 401) {
      localStorage.removeItem('cm_token');
      localStorage.removeItem('cm_user');
    }

    // 451 (pending legal acceptance) → window event consumed by
    // LegalAcceptanceGuard; event bus keeps this module React-free.
    if (response.status === 451) {
      try {
        const cloned = response.clone();
        const body = await cloned.json();
        if (body?.code === 'pending_legal_acceptance') {
          window.dispatchEvent(
            new CustomEvent('cm:pending-legal-acceptance', { detail: body })
          );
        }
      } catch {
        /* non-fatal */
      }
    }

    return response;
  }

  private async request<T = any>(
    url: string,
    method = 'GET',
    body?: any,
    opts: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const response = await this.requestRaw(url, method, body, opts);

    // opts.responseType only applies to OK responses; errors parse as
    // JSON/text (FastAPI returns JSON failures even for blob requests).
    const ct = response.headers.get('content-type') || '';
    let responseType: ResponseType;
    if (response.ok && opts.responseType) {
      responseType = opts.responseType;
    } else {
      responseType = ct.includes('application/json') ? 'json' : 'text';
    }

    let data: T;
    if (responseType === 'blob') {
      data = (await response.blob()) as any;
    } else if (responseType === 'json') {
      data = await response.json();
    } else {
      data = (await response.text()) as any;
    }

    if (!response.ok) {
      const detail = (data as any)?.detail || response.statusText;
      throw new ApiError(detail, response.status);
    }

    return { data, status: response.status };
  }

  get<T = any>(url: string, opts?: RequestOptions) {
    return this.request<T>(url, 'GET', undefined, opts);
  }
  post<T = any>(url: string, data?: any, opts?: RequestOptions) {
    return this.request<T>(url, 'POST', data, opts);
  }
  put<T = any>(url: string, data?: any, opts?: RequestOptions) {
    return this.request<T>(url, 'PUT', data, opts);
  }
  patch<T = any>(url: string, data?: any, opts?: RequestOptions) {
    return this.request<T>(url, 'PATCH', data, opts);
  }
  del<T = any>(url: string, opts?: RequestOptions) {
    return this.request<T>(url, 'DELETE', undefined, opts);
  }

  /** GET a binary blob (PDF, .pkpass, image) with the same auth + error semantics. */
  async getBlob(url: string, opts?: RequestOptions): Promise<Blob> {
    const { data } = await this.request<Blob>(url, 'GET', undefined, { ...opts, responseType: 'blob' });
    return data;
  }

  /** POST FormData. Content-Type deliberately unset — browser adds the multipart boundary. */
  postFormData<T = any>(url: string, formData: FormData, opts?: RequestOptions) {
    return this.request<T>(url, 'POST', formData, opts);
  }
}

const httpClient = new HttpClient();
export default httpClient;
export { HttpClient };
